import { FC } from 'react'

import { useForm } from '@tanstack/react-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Request } from 'commons/utils/request'
import {
  DatePicker,
  Dialog,
  DialogFace,
  DialogFooterActions,
  FF,
  Toast,
  handleFormSubmissionError
} from 'ui/components'
import { useConfirmation } from 'ui/components/Dialog/ConfirmationDialogV2'
import { Col, Row } from 'ui/themes/globalStyles'

import { HRR_DETAILS_KEY } from '../../../../query'

type UpdatePeriodPayload = { id: number; endDate: string | null; startDate: string | null }

type DialogProps = UpdatePeriodPayload & { onClose: () => void }

export const UpdatePeriodDialog: FC<DialogProps> = ({ onClose, id, startDate, endDate }) => {
  const queryClient = useQueryClient()
  const confirmation = useConfirmation()

  const { mutate, isPending } = useMutation<{ id: number }, Error, UpdatePeriodPayload>({
    mutationFn: (opts) => Request.put(Request.urls.bank.hrrSavePeriod, opts),
    onSuccess: () => {
      Toast.successSmall(`Period was updated successfully`)
      queryClient.invalidateQueries({ queryKey: [HRR_DETAILS_KEY] })
      onClose()
    },
    onError: (error) => {
      handleFormSubmissionError(error, formApi)
    }
  })
  const formApi = useForm({
    defaultValues: { id, startDate, endDate } as UpdatePeriodPayload,
    onSubmit: ({ value }) => mutate(value)
  })

  const handleUpdatePeriod = async () => {
    await confirmation.showAskConfirmation({
      message: 'The High-Risk Period will be recalculated based on the chosen period. Do you wish to continue?',
      submitButtonText: 'Confirm',
      confirmCallback: formApi.handleSubmit
    })
  }

  return (
    <Dialog
      id="hrr-update-period-dialog"
      onClose={onClose}
      face={DialogFace.PRIMARY}
      title="Update Period"
      footerActions={[DialogFooterActions.SAVE, DialogFooterActions.CANCEL]}
      onSubmit={handleUpdatePeriod}
      isSubmitting={isPending}
    >
      <div style={{ padding: '27px 42px 27px 40px' }}>
        <Row>
          <Col>
            <formApi.Field
              name="startDate"
              children={(filesField) => (
                <FF
                  field={filesField}
                  label="Start Date"
                  input={(inputProps) => <DatePicker {...inputProps} required />}
                />
              )}
            />
          </Col>
          <Col>
            <formApi.Field
              name="endDate"
              children={(filesField) => (
                <FF
                  field={filesField}
                  label="End Date"
                  input={(inputProps) => <DatePicker {...inputProps} required />}
                />
              )}
            />
          </Col>
        </Row>
      </div>
    </Dialog>
  )
}
